import React, { useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { trackLead } from "@/lib/analytics";
import PayRailModal from "@/components/PayRailModal";

const CASE_TYPES = [
  "Divorce / Asset Discovery",
  "Corporate Fraud Recovery",
  "Estate / Probate (Legacy Wallet)",
  "Lost / Corrupted Seed Phrase",
  "Exchange Insolvency Claim",
  "Ransomware / Extortion Tracing",
  "Other",
];

const ASSET_RANGES = [
  "Under $100K",
  "$100K – $500K",
  "$500K – $2M",
  "$2M – $10M",
  "$10M+",
];

const FEASIBILITY_SERVICE = {
  id: "feasibility_audit",
  name: "Wallet Feasibility Audit",
  amount: 250,
  amount_display: "$250",
};

export default function InquiryForm() {
  const [submitting, setSubmitting] = useState(false);
  const [showPayModal, setShowPayModal] = useState(false);
  const [leadId, setLeadId] = useState(null);
  const [form, setForm] = useState({
    name: "", email: "", firm: "", role: "", case_type: "",
    jurisdiction: "", asset_range: "", description: "", phone: "",
  });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.case_type || !form.description) {
      toast.error("Please complete the required fields.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/leads`, form);
      setLeadId(data.id);
      trackLead({ caseType: form.case_type, assetRange: form.asset_range });
      setShowPayModal(true);
      toast.success("Inquiry filed. Proceed to retainer.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="inquiry" className="paper-section relative" data-testid="inquiry-section">
      <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
        <div className="grid grid-cols-12 gap-10">
          <div className="col-span-12 lg:col-span-4">
            <div className="mono-label-paper mb-6">§ 05 · CASE INTAKE</div>
            <h2 className="font-serif-display text-4xl sm:text-5xl leading-[1.1]">
              File a Case Feasibility Inquiry.
            </h2>
            <p className="text-paperink/75 mt-8 text-sm leading-relaxed">
              Submission is treated as privileged attorney work-product intake.
            </p>
            <div className="mt-10 space-y-4 border-t border-paperline pt-6">
              {[
                "Attorney-work-product channel",
                "No marketing, no list-building",
                "48-hour memorandum turnaround",
                "Fixed-fee, flat engagement",
              ].map((x) => (
                <div key={x} className="flex items-center gap-3 text-paperink/85 text-sm">
                  <span className="w-3 h-px bg-paperink/40" />
                  {x}
                </div>
              ))}
            </div>
          </div>

          <div className="col-span-12 lg:col-span-7 lg:col-start-6">
            <div className="border border-paperline bg-paper p-8 lg:p-12">
              <form onSubmit={submit} className="space-y-8" data-testid="inquiry-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8">
                  <Field label="Full Name *">
                    <input className="input-paper" required value={form.name} onChange={(e) => update("name", e.target.value)} data-testid="input-name" />
                  </Field>
                  <Field label="Email *">
                    <input type="email" className="input-paper" required value={form.email} onChange={(e) => update("email", e.target.value)} data-testid="input-email" />
                  </Field>
                  <Field label="Firm / Organisation">
                    <input className="input-paper" value={form.firm} onChange={(e) => update("firm", e.target.value)} data-testid="input-firm" />
                  </Field>
                  <Field label="Role">
                    <input className="input-paper" placeholder="Partner, Investigator, Executor…" value={form.role} onChange={(e) => update("role", e.target.value)} data-testid="input-role" />
                  </Field>
                  <Field label="Phone (optional)">
                    <input className="input-paper" value={form.phone} onChange={(e) => update("phone", e.target.value)} data-testid="input-phone" />
                  </Field>
                  <Field label="Jurisdiction">
                    <input className="input-paper" placeholder="e.g. SDNY, UK, Ontario" value={form.jurisdiction} onChange={(e) => update("jurisdiction", e.target.value)} data-testid="input-jurisdiction" />
                  </Field>
                  <Field label="Case Type *">
                    <select className="input-paper" required value={form.case_type} onChange={(e) => update("case_type", e.target.value)} data-testid="input-case-type">
                      <option value="">— Select —</option>
                      {CASE_TYPES.map((c) => (<option key={c} value={c}>{c}</option>))}
                    </select>
                  </Field>
                  <Field label="Asset Range">
                    <select className="input-paper" value={form.asset_range} onChange={(e) => update("asset_range", e.target.value)} data-testid="input-asset-range">
                      <option value="">— Select —</option>
                      {ASSET_RANGES.map((c) => (<option key={c} value={c}>{c}</option>))}
                    </select>
                  </Field>
                </div>

                <Field label="Matter Description * (redact as needed)">
                  <textarea
                    className="input-paper"
                    required
                    placeholder="Outline the asset class, known counterparties, and desired work-product. Do not include privileged material."
                    value={form.description}
                    onChange={(e) => update("description", e.target.value)}
                    data-testid="input-description"
                  />
                </Field>

                <div className="pt-4 border-t border-paperline flex justify-end">
                  <button type="submit" disabled={submitting} className="btn-paper-primary" data-testid="submit-inquiry-btn">
                    {submitting ? "Filing…" : "Start the recovery inquiry now"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {showPayModal && (
        <PayRailModal
          service={FEASIBILITY_SERVICE}
          leadId={leadId}
          onClose={() => setShowPayModal(false)}
        />
      )}
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mono-label-paper block mb-3">{label}</span>
      {children}
    </label>
  );
}
